<template>
  <div v-if="data.detail" class="modal-backdrop" @click.self="data.closeMessage">
    <section class="modal message-detail-modal">
      <header>
        <div>
          <span class="eyebrow">记录详情</span>
          <h3>{{ statusTitle }}</h3>
        </div>
        <button class="icon-button" title="关闭" aria-label="关闭记录详情" @click="data.closeMessage"><X :size="18" /></button>
      </header>

      <div class="detail-body">
        <section class="status-summary">
          <div><strong>{{ derivedCount }}</strong><span>个整理结果</span></div>
          <div><strong>{{ data.detail.message.status === "archived" ? "已归档" : derivedCount ? "已整理" : "原文已保存" }}</strong><span>当前状态</span></div>
          <div><strong>{{ data.detail.message.channel_name || "原点" }}</strong><span>所在频道</span></div>
        </section>

        <section>
          <h4>原始记录</h4>
          <p class="original">{{ messageDisplayText(data.detail.message, data.detail.attachments) }}</p>
          <span>{{ formatDate(data.detail.message.created_at) }}</span>
        </section>

        <section v-if="data.detail.attachments.length">
          <h4>附件</h4>
          <div class="attachment-grid">
            <article v-for="attachment in data.detail.attachments" :key="attachment.id" class="attachment-chip">
              <button
                class="attachment-preview-trigger attachment-grid-trigger"
                :title="`预览 ${attachment.filename}`"
                @click="selectedAttachment = attachment"
              >
                <img v-if="imageUrls[attachment.id]" :src="imageUrls[attachment.id]" alt="" />
                <Paperclip v-else :size="16" />
                <span>{{ attachment.filename }}</span>
              </button>
              <button title="下载附件" aria-label="下载附件" @click="run(() => data.downloadAttachment(attachment))"><Download :size="14" /></button>
            </article>
          </div>
        </section>

        <section>
          <div class="section-title">
            <h4>整理结果</h4>
            <span v-if="data.detail.message.ai_status === 'pending' || data.detail.message.ai_status === 'processing'">自动整理中</span>
            <span v-else-if="data.detail.message.ai_status === 'failed'">整理失败，可重试</span>
            <span v-else-if="data.detail.message.ai_needs_confirmation">待确认</span>
            <span v-else-if="derivedCount === 0">尚未整理</span>
          </div>
          <p v-if="data.detail.message.ai_summary" class="empty">{{ data.detail.message.ai_summary }}</p>
          <button v-if="data.detail.message.ai_status === 'failed'" @click="run(data.retryAi)">重新自动整理</button>
          <div v-if="derivedCount" class="derived-list">
            <article v-for="todo in data.detail.todos" :key="todo.id" class="derived-item">
              <CircleCheck :size="17" />
              <div><strong>{{ todo.title }}</strong><span>{{ priorityLabel(todo.priority) }}{{ todo.due_at ? ` · ${formatDate(todo.due_at)}` : "" }}</span></div>
            </article>
            <article v-for="reminder in data.detail.reminders" :key="reminder.id" class="derived-item">
              <Bell :size="17" />
              <div><strong>{{ reminder.title }}</strong><span>{{ formatDate(reminder.remind_at) }}</span></div>
            </article>
            <article v-for="schedule in data.detail.schedules" :key="schedule.id" class="derived-item">
              <CalendarPlus :size="17" />
              <div><strong>{{ schedule.title }}</strong><span>{{ formatDate(schedule.start_at) }}</span></div>
            </article>
            <article v-for="project in data.detail.projects" :key="project.id" class="derived-item">
              <FolderOpen :size="17" />
              <div><strong>{{ project.name }}</strong><span>项目关联</span></div>
              <button title="移除项目关联" aria-label="移除项目关联" @click.stop="run(() => data.removeProjectMessage(project.id))"><Unlink :size="14" /></button>
            </article>
          </div>
          <p v-else class="empty">选择下方动作，把这条原文转成待办、提醒、日程或项目。</p>
        </section>

        <section>
          <label>
            整理备注
            <textarea v-model="data.detailEdit.organizing_note" rows="3" placeholder="补充只有你自己需要记住的上下文" />
          </label>
          <button class="primary compact-action" @click="run(data.saveDetail)"><Save :size="17" />保存备注</button>
        </section>

        <section class="organize-section">
          <h4>整理这条记录</h4>
          <div class="action-picker" role="group" aria-label="选择整理动作">
            <button :class="{ active: selectedAction === 'todo' }" @click="selectedAction = 'todo'"><CircleCheck :size="17" />转待办</button>
            <button :class="{ active: selectedAction === 'reminder' }" @click="selectedAction = 'reminder'"><Bell :size="17" />转提醒</button>
            <button :class="{ active: selectedAction === 'schedule' }" @click="selectedAction = 'schedule'"><CalendarPlus :size="17" />转日程</button>
            <button :class="{ active: selectedAction === 'project' }" @click="selectedAction = 'project'"><FolderOpen :size="17" />加入项目</button>
          </div>

          <div v-if="selectedAction" class="progressive-form">
            <template v-if="selectedAction === 'todo'">
              <label>
                截止时间（可选）
                <input v-model="data.detailTodoDueAt" type="datetime-local" />
              </label>
              <label>
                优先级
                <AppSelect v-model="data.detailTodoPriority" :options="priorityOptions" aria-label="待办优先级" />
              </label>
            </template>
            <label v-if="selectedAction === 'reminder'">
              提醒时间
              <input v-model="data.detailReminderAt" type="datetime-local" />
            </label>
            <template v-if="selectedAction === 'schedule'">
              <label>
                开始时间
                <input v-model="data.detailScheduleStartAt" type="datetime-local" />
              </label>
              <label>
                结束时间（可选）
                <input v-model="data.detailScheduleEndAt" type="datetime-local" />
              </label>
              <label v-if="isAndroid" class="inline-check">
                <input v-model="data.detailScheduleAlarmEnabled" type="checkbox" />
                同时设置 Android 闹钟
              </label>
            </template>
            <label v-if="selectedAction === 'project'">
              项目
              <AppSelect v-model="data.detailProjectId" :options="projectOptions" placeholder="选择项目" aria-label="选择项目" />
            </label>
            <button class="primary" :disabled="actionDisabled" @click="applyAction">
              <Check :size="17" />确认{{ actionLabel }}
            </button>
          </div>
        </section>

        <section class="archive-section">
          <h4>其他操作</h4>
          <button @click="run(() => data.detailAction('archive'))"><Archive :size="17" />归档记录</button>
        </section>

        <section class="danger-zone detail-danger-zone">
          <h4>删除原文</h4>
          <p>会同时删除 {{ data.detail.todos.length }} 个待办、{{ data.detail.reminders.length }} 个提醒、{{ data.detail.schedules.length }} 个日程，并移除 {{ data.detail.projects.length }} 个项目关联。</p>
          <button class="danger-button" @click="confirmDelete"><Trash2 :size="17" />删除原文及关联结果</button>
        </section>
      </div>
    </section>
  </div>
  <AttachmentPreviewModal :attachment="selectedAttachment" @close="selectedAttachment = null" />
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { Capacitor } from "@capacitor/core";
import { Archive, Bell, CalendarPlus, Check, CircleCheck, Download, FolderOpen, Paperclip, Save, Trash2, Unlink, X } from "@lucide/vue";
import { api } from "../api";
import AttachmentPreviewModal from "./AttachmentPreviewModal.vue";
import AppSelect from "./AppSelect.vue";
import { formatDate, messageDisplayText } from "../composables/useFormat";
import { useDataStore } from "../stores/data";
import type { Attachment, Todo } from "../types";

const data = useDataStore();
const selectedAction = ref<"todo" | "reminder" | "schedule" | "project" | "">("");
const imageUrls = ref<Record<string, string>>({});
const selectedAttachment = ref<Attachment | null>(null);
const isAndroid = Capacitor.getPlatform() === "android";
const derivedCount = computed(() => data.detail
  ? data.detail.todos.length + data.detail.reminders.length + data.detail.schedules.length + data.detail.projects.length
  : 0);
const statusTitle = computed(() => {
  if (!data.detail) return "";
  if (data.detail.message.status === "archived") return "这条记录已归档";
  if (derivedCount.value) return "这条记录已经整理";
  return "原文已安全保存";
});
const actionLabel = computed(() => ({ todo: "转为待办", reminder: "设置提醒", schedule: "创建日程", project: "加入项目", "": "" })[selectedAction.value]);
const actionDisabled = computed(() =>
  (selectedAction.value === "reminder" && !data.detailReminderAt)
  || (selectedAction.value === "schedule" && !data.detailScheduleStartAt)
  || (selectedAction.value === "project" && !data.detailProjectId));
const priorityOptions = [
  { value: "normal", label: "普通" },
  { value: "high", label: "高" },
  { value: "low", label: "低" }
];
const projectOptions = computed(() => [
  { value: "", label: "选择项目" },
  ...data.projects.map((project) => ({ value: project.id, label: project.name }))
]);

onMounted(() => window.addEventListener("keydown", onKeydown));
onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown);
  clearImageUrls();
});
watch(
  () => (data.detail ? data.detail.attachments.map((item) => item.id).join(",") : ""),
  () => {
    selectedAction.value = "";
    selectedAttachment.value = null;
    loadImageUrls();
  },
  { immediate: true }
);

function onKeydown(event: KeyboardEvent) {
  if (event.key === "Escape" && data.detail) data.closeMessage();
}

async function run(action: () => Promise<unknown> | void) {
  await action();
}

async function applyAction() {
  if (selectedAction.value === "project") await data.attachDetailProject();
  else if (selectedAction.value) await data.detailAction(selectedAction.value);
  selectedAction.value = "";
}

async function confirmDelete() {
  if (!data.detail) return;
  const impact = `删除原文将同时删除 ${data.detail.todos.length} 个待办、${data.detail.reminders.length} 个提醒、${data.detail.schedules.length} 个日程，并移除 ${data.detail.projects.length} 个项目关联。继续？`;
  if (!window.confirm(impact)) return;
  await data.deleteCurrentMessage();
}

function priorityLabel(priority: Todo["priority"]) {
  return { high: "高优先级", normal: "普通优先级", low: "低优先级" }[priority || "normal"];
}

async function loadImageUrls() {
  clearImageUrls();
  const attachments = data.detail?.attachments.filter((item) => item.mime_type.startsWith("image/")) || [];
  for (const attachment of attachments) {
    const blob = await api.attachmentBlob(attachment.id).catch(() => null);
    if (blob) imageUrls.value[attachment.id] = URL.createObjectURL(blob);
  }
}

function clearImageUrls() {
  Object.values(imageUrls.value).forEach((url) => URL.revokeObjectURL(url));
  imageUrls.value = {};
}
</script>
